/**
 * The Executive overview's AI slot, wired — Task 57's *"Nối slot AI ở Executive
 * overview (Task 44)"*.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  IT RENDERS `InsightCard`, NOT A TILE
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * The obvious dashboard design is a tile with the model's sentence in it and a link
 * to the full page. That design is the defect this whole task exists to prevent: a
 * sentence about somebody's working day, trimmed to fit, with its `limitations`
 * left on the page the reader did not open.
 *
 * So the slot reads **one** insight and hands it to the same component the AI Insights
 * screen uses. The caveat travels with the claim because it is the same subtree, not
 * because somebody remembered to copy it across. `screens/ai/one-renderer.test.tsx`
 * asserts that as a set: exactly one file in `web/src` renders a reading's headline.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  AND THE DISCLAIMER STAYS, ABOVE IT
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * Task 44 put it here while the slot was empty, on the grounds that "a disclaimer
 * that appears only once the panel has content is a disclaimer that is missing on the
 * first day it matters". It is now above content rather than above nothing, which is
 * the day it was written for.
 *
 * ── A refusal is a refusal, an empty window is an empty window ──────────────
 *
 * `use_ai_analytics` is checked before the read is issued at all, because the slot
 * sits on a screen many sessions can open and firing a `403` for each of them would
 * teach people that refusals are background noise. A session without it gets a
 * sentence and **no request**, asserted. A budget refusal reaching this slot renders
 * `RefusalPanel`'s budget branch — the same words as on the full screen, because a
 * company that has spent its month must not read "no insights" on the dashboard.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { aiInsightsPath } from '../../api/paths';
import type { AiInsightListResponse, DashboardPermission } from '../../api/types';
import { useApiQuery } from '../../api/useApiQuery';
import { readAiRefusal } from '../../ai/refusal';
import { Skeleton } from '../../states/Skeleton';
import { StatusNote } from '../../states/StatusNote';
import { AiDisclaimer } from '../ai/AiDisclaimer';
import { InsightCard } from '../ai/InsightCard';
import { RefusalPanel } from '../ai/RefusalPanel';

export interface AiSlotProps {
  readonly permissions: readonly DashboardPermission[];
  readonly from: string;
  readonly to: string;
}

export function AiSlot({ permissions, from, to }: AiSlotProps): ReactNode {
  const { t } = useI18n();
  const permitted = permissions.includes('workforce.use_ai_analytics');
  // `null` is the hook's own "there is nothing to ask yet" — no request, no 403, no
  // spinner. Not an empty result, which would be a claim about the company.
  // Named `requestPath` rather than `path`: `screens/overview-privacy.test.tsx` forbids
  // the bare identifier anywhere under this directory, because `path` is one of the
  // eight activity fields no overview component may read. The guard is right to be
  // strict there, and renaming a local is the cheaper side of that trade than
  // loosening a privacy guard on the executive screen.
  const requestPath = permitted ? aiInsightsPath({ limit: 1, dateFrom: from, dateTo: to }) : null;
  const query = useApiQuery<AiInsightListResponse>(requestPath);
  const row = query.data?.data[0];

  return (
    <section className="wp-section" data-testid="k4-ai-slot" aria-labelledby="k4-ai-slot-title">
      <h2 className="wp-section__title" id="k4-ai-slot-title">
        {t('m4.overview.title')}
      </h2>

      {/* Above the content, and unhideable, exactly as on /ai-insights. */}
      <AiDisclaimer testId="k4-ai-disclaimer" />

      <p className="wp-small">{t('m4.overview.body')}</p>

      {!permitted ? (
        <StatusNote severity="info" title={t('m4.overview.denied')} testId="k4-ai-slot-denied" />
      ) : query.status === 'loading' ? (
        <Skeleton rows={2} label={t('m4.subject.insights')} testId="k4-ai-slot-skeleton" />
      ) : query.status === 'error' && query.error !== null ? (
        <RefusalPanel
          refusal={readAiRefusal(query.error)}
          error={query.error}
          subject={t('m4.subject.insights')}
          onRetry={query.reload}
        />
      ) : row === undefined ? (
        <StatusNote severity="info" title={t('m4.overview.empty')} testId="k4-ai-slot-empty" />
      ) : (
        <InsightCard row={row as unknown as Readonly<Record<string, unknown>>} />
      )}

      <p className="wp-small">
        <a className="wp-linklike" href="/ai-insights" data-testid="k4-ai-slot-link">
          {t('m4.overview.link')}
        </a>
      </p>
    </section>
  );
}
